import React, { useState, useEffect, useMemo } from 'react';
import './requiredStock.scss';
import { ModalDetails, RequiredStockData } from 'business/models/CustomModels';
import { getIngredientStock, isNullOrUndefined, showAlertSnackBar } from 'utils/Global';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { printIngredientBarcode } from 'business/services/IngredientService';
import { useRMSelector } from 'redux/hooks';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { RMModal } from 'components/modal';
import { useLastSelectedWellNavigationDetails } from 'redux/hooks/experiment';
import { useShowLabelPrinterError } from 'CustomHooks';
const headerIconsColor = '#313131';

export interface IStockRowProps {
  ingredientStockId: any;
  name: any;
  stocknum: any;
  ph: any;
  volume: any;
  vendorpartnum: any;
}

export const StockRow = (props: IStockRowProps) => {
  const { ingredientStockId, name, stocknum, ph, volume, vendorpartnum } = props;
  const showLabelPrinterError = useShowLabelPrinterError();
  const [modalDetails, setModalDetails] = useState<ModalDetails>({
    showModal: false,
    setShowModal: () => {},
    onOk: () => {},
    body: null,
    heading: '',
    isOkOnly: false,
    okButtonText: undefined,
    noButtonText: undefined,
    showButtons: true,
    dialogClassName: '',
    isCancelOnly: false,
  });

  const printBarcode = async () => {
    showAlertSnackBar(
      EAlertType.INFO,
      ESnackBarPosition.TOP,
      'Barcode has been sent for printing.',
    );
    const response = await printIngredientBarcode(ingredientStockId);
    if (response.value != '') {
      showLabelPrinterError();
    }
  };
  return (
    <>
      <RMModal id='commandpanel-requiredstock-stockrow-modal'
        showModal={modalDetails.showModal}
        isCancelOnly={modalDetails.isCancelOnly}
        onOk={modalDetails.onOk}
        body={modalDetails.body}
        heading={modalDetails.heading}
        setShowModal={() => setModalDetails({ ...modalDetails, showModal: false })}
        dialogClassName={modalDetails.dialogClassName}
      />
      <div id="commandpanel-requiredstock-stockrow-wrapper" 
        className="required-stock-container">
        <div id="commandpanel-requiredstock-stockrow-icon-wrapper">
          <RMSvgIcon
            id="commandpanel-requiredstock-stockrow-icon"
            name="IngredientTubes"
            width={30}
            height={30}
            style={{ paddingInline: '1%' }}
          />
        </div>
        <div id="commandpanel-requiredstock-stockrow-info-wrapper" style={{ display: 'flex', flexDirection: 'column', width: '80%' }}>
          <div id="commandpanel-requiredstock-stockrow-name-wrapper" style={{ display: 'flex' }}>
            <text id={`commandpanel-requiredstock-stockrow-${name}`} className="marginRight5">
              {name} {ph ? 'pH ' + parseFloat(ph.toFixed(2)) : ''}
            </text>
          </div>
          <div id="commandpanel-requiredstock-stockrow-details-wrapper" style={{ display: 'flex' }}>
            <div id={`commandpanel-requiredstock-stockrow-partnumber-${vendorpartnum}`} className="flex-90-left">Part # {vendorpartnum}</div>
            <div id={`commandpanel-requiredstock-stockrow-stocknumber-${stocknum}`}className="flex-90-center">Stock {stocknum}</div>
            <div id={`commandpanel-requiredstock-stockrow-volume-${volume}`}className="flex-90-right">{volume}</div>
          </div>
        </div>
        <div>
          <RMCustomButton
            Icon={
              <RMSvgIcon
                id='commandpanel-requirdstock-stockrow-printbarcode-icon'
                name="PrintBarcode"
                width="25px"
                height="25px"
                fill={headerIconsColor}
              />
            }
            onClick={printBarcode}
            isDisabled={false}
            showBorder={false}
            contentContainerClass={''}
          ></RMCustomButton>
        </div>
      </div>
    </>
  );
};

export default function RequiredStock() {
  const [rsInfo, setRequiredStockInfo] = useState(null);
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const { plateID } = wellNavigationDetails || {};
  const exp = useRMSelector(selectExp);

  const plate = useMemo(() => {
    if (!exp.plateDto) {
      return;
    }
    const plate = exp.plateDto.find((pl) => pl.plateId === plateID);
    if (plate === null || plate === undefined) {
      return exp.plateDto[0];
    }
    return plate;
  }, [exp.plateDto, plateID]);

  function formatData(data) {
    const rslist = data
      .sort(function (a, b) {
        return b.volume - a.volume;
      })
      .map((rsin) => {
        if (
          !isNullOrUndefined(rsin.stockNumber) &&
          !isNullOrUndefined(rsin.id) &&
          rsin.volume > 0
        ) {
          return (
            <div id={`commandpanel-requiredstock-formatdata-wrapper`} key={rsin.id} style={{ paddingTop: '2%' }}>
              <StockRow
                ingredientStockId={rsin.ingredientStockId}
                name={`${rsin.stockConcentration} ${rsin.concentrationUnits} ${rsin.name}`}
                ph={rsin.pH}
                stocknum={`${rsin.id.toString().padStart(5, 0)}-${rsin.stockNumber
                  .toString()
                  .padStart(3, 0)}`}
                vendorpartnum={rsin.vendorpartnum}
                volume={`${(rsin.volume / 1000).toFixed(2)} mL`}
              />
            </div>
          );
        }
      });
    setRequiredStockInfo(null);
    setRequiredStockInfo(rslist);
  }
  function getRequiredStockFromPlateVm() {
    const rsData: RequiredStockData = new RequiredStockData();

    if (plate.ingredients === undefined || plate.ingredients === null) {
      return null;
    }
    plate.ingredients.wellList.forEach((well) => {
      rsData.H2O.ingredientStockId = 1;
      rsData.H2O.stockConcentration = 100;
      rsData.H2O.concentrationUnits = '%';
      rsData.H2O.name = 'Water';
      rsData.H2O.id = 1;
      rsData.H2O.pH = null;
      rsData.H2O.stockNumber = 1;
      rsData.H2O.vendorpartnum = null;
      if (rsData.H2O.volume === undefined) {
        rsData.H2O.volume = 0;
      }
      rsData.H2O.volume += well.wellWaterVolume;

      well.wellLayerList.forEach((wellLayer) => {
        wellLayer.wellLayerIngredientList.forEach((ingredient) => {
          let shortName = ingredient.ingredientStockIngredient.shortName;
          if (ingredient.ingredientTypeIsBuffer) {
            shortName = shortName + 0;
            if (rsData[shortName] === undefined) {
              rsData[shortName] = {};
            }
            rsData[shortName].ingredientStockId = ingredient.ingredientStock.id;
            rsData[shortName].stockConcentration =
              ingredient.ingredientStock.stockConcentration;
            rsData[shortName].concentrationUnits =
              ingredient.ingredientStock.concentrationUnits;
            rsData[shortName].name = ingredient.ingredientStockIngredient.longName;
            rsData[shortName].id = ingredient.ingredientStockIngredient.id;
            rsData[shortName].pH = ingredient.ingredientStock.stockPh;
            rsData[shortName].stockNumber = ingredient.ingredientStock.stockNumber;
            rsData[shortName].vendorpartnum = ingredient.ingredientStock.vendorPartNumber;

            if (rsData[shortName].volume === undefined) {
              rsData[shortName].volume = 0;
            }
            rsData[shortName].volume += ingredient.volume;

            if (!isNullOrUndefined(ingredient.highPhIngStockId)) {
              const highPHIngStock = getIngredientStock(ingredient.highPhIngStockId);
              shortName = shortName + 1;
              if (rsData[shortName] === undefined) {
                rsData[shortName] = {};
              }
              rsData[shortName].ingredientStockId = ingredient.ingredientStock.id;
              rsData[shortName].stockConcentration = highPHIngStock.stockConcentration;
              rsData[shortName].concentrationUnits = highPHIngStock.concentrationUnits;
              rsData[shortName].name = ingredient.ingredientStockIngredient.longName;
              rsData[shortName].id = ingredient.ingredientStockIngredient.id;
              rsData[shortName].pH = highPHIngStock.stockPh;
              rsData[shortName].stockNumber = highPHIngStock.stockNumber;
              rsData[shortName].vendorPartNumber = highPHIngStock.vendorPartNumber;

              if (rsData[shortName].volume === undefined) {
                rsData[shortName].volume = 0;
              }
              rsData[shortName].volume += ingredient.volume;
            }
          } else {
            if (rsData[shortName] === undefined) {
              rsData[shortName] = {};
            }
            rsData[shortName].ingredientStockId = ingredient.ingredientStock.id;
            rsData[shortName].stockConcentration =
              ingredient.ingredientStock.stockConcentration;
            rsData[shortName].concentrationUnits =
              ingredient.ingredientStock.concentrationUnits;
            rsData[shortName].name = ingredient.ingredientStockIngredient.longName;
            rsData[shortName].id = ingredient.ingredientStockIngredient.id;
            rsData[shortName].pH = ingredient.pH;
            rsData[shortName].stockNumber = ingredient.ingredientStock.stockNumber;
            rsData[shortName].vendorpartnum = ingredient.ingredientStock.vendorPartNumber;

            if (rsData[shortName].volume === undefined) {
              rsData[shortName].volume = 0;
            }
            rsData[shortName].volume += ingredient.volume;
          }
        });
      });
    });
    return rsData;
  }
  useEffect(() => {
    const rsList = getRequiredStockFromPlateVm();
    if (rsList !== null) {
      formatData(Object.values(rsList));
    }
  }, [plate.plateId]);

  return (
    <div
      id='commandpanel-requirestock-wrapper'
      style={{ overflow: 'auto', height: '97%', paddingTop: '3%', paddingBottom: '3%' }}
    >
      {rsInfo}
    </div>
  );
}
